import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, X, Loader2, Send, CheckCircle2 } from "lucide-react";

/**
 * Contact button + modal — landing-page "talk to Ashlee" entry point.
 *
 * Submits to POST /api/contact which stores the message and forwards via
 * Resend to ashlee@asvmedicalservices.com. Honeypot field (company) is
 * styled off-screen so bots fill it; humans don't see it and submissions
 * with anything in it are silently dropped server-side.
 */
export default function ContactButton({ label = "Contact", onDark = false }) {
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", subject: "", message: "", company: "",
    });

    const reset = () => {
        setForm({ name: "", email: "", subject: "", message: "", company: "" });
        setDone(false);
    };
    const close = () => { setOpen(false); setTimeout(reset, 400); };

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        const { name, email, message } = form;
        if (!name.trim() || !email.trim() || message.trim().length < 5) {
            toast.error("Please fill in your name, email, and a brief message.");
            return;
        }
        setSending(true);
        try {
            await api.post("/contact", {
                name: name.trim(),
                email: email.trim(),
                subject: form.subject.trim() || "Landing page contact",
                message: message.trim(),
                company: form.company,  // honeypot
            });
            setDone(true);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("Too many messages from this connection — try again in an hour.");
            } else {
                toast.error("Couldn't send your message. Please email ashlee@asvmedicalservices.com directly.");
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid="contact-button"
                className={`inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${onDark ? "text-white/85 hover:text-white" : "text-[#4C5551] hover:text-teal-dark"}`}
            >
                <Mail size={14} strokeWidth={1.8} /> {label}
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-labelledby="contact-modal-title"
                    className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-black/40 backdrop-blur-sm"
                    onClick={close}
                    data-testid="contact-modal"
                >
                    <div
                        className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <header className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-cream-light">
                            <div className="flex items-center gap-2">
                                <span className="rounded-full bg-teal-soft text-teal-dark p-1.5">
                                    <Mail size={14} strokeWidth={2} />
                                </span>
                                <h2 id="contact-modal-title" className="font-heading text-lg font-semibold text-[#1F2624]">
                                    {done ? "Message sent." : "Talk to Ashlee"}
                                </h2>
                            </div>
                            <button
                                onClick={close}
                                aria-label="Close"
                                data-testid="contact-close"
                                className="-m-2 p-2 text-[#6E7A74] hover:text-[#4C5551]"
                            >
                                <X size={18} strokeWidth={1.8} />
                            </button>
                        </header>

                        {done ? (
                            <div className="p-6 text-center space-y-3" data-testid="contact-success">
                                <CheckCircle2 size={36} strokeWidth={1.6} className="text-teal-dark mx-auto" />
                                <p className="text-[#1F2624] font-medium">Thank you — we got your note.</p>
                                <p className="text-sm text-[#4C5551]">
                                    Ashlee personally reads every message and replies within 24-48 hours.
                                </p>
                                <button onClick={close} className="ml-btn-primary mt-2">Close</button>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="p-5 sm:p-6 space-y-3" data-testid="contact-form">
                                <p className="text-sm text-[#4C5551]">
                                    Questions about Memory Lane, partnership ideas, press, or anything else —
                                    Ashlee reads every message personally.
                                </p>
                                <Field label="Your name" testid="contact-name">
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                        required
                                        autoFocus
                                        className="ml-input"
                                        data-testid="contact-name-input"
                                    />
                                </Field>
                                <Field label="Email" testid="contact-email">
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                                        required
                                        className="ml-input"
                                        data-testid="contact-email-input"
                                    />
                                </Field>
                                <Field label="Subject (optional)" testid="contact-subject">
                                    <input
                                        type="text"
                                        value={form.subject}
                                        onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                                        maxLength={200}
                                        className="ml-input"
                                        placeholder="What's this about?"
                                        data-testid="contact-subject-input"
                                    />
                                </Field>
                                <Field label="Message" testid="contact-message">
                                    <textarea
                                        rows={5}
                                        value={form.message}
                                        onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                                        required
                                        minLength={5}
                                        maxLength={4000}
                                        className="ml-input resize-y"
                                        placeholder="Tell us what you're working on or what you'd like to know."
                                        data-testid="contact-message-input"
                                    />
                                </Field>
                                {/* Honeypot — hidden from humans, irresistible to bots */}
                                <div aria-hidden="true" className="absolute left-[-9999px] top-auto w-1 h-1 overflow-hidden">
                                    <label>
                                        Company (do not fill)
                                        <input
                                            type="text"
                                            tabIndex={-1}
                                            autoComplete="off"
                                            value={form.company}
                                            onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                                        />
                                    </label>
                                </div>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="ml-btn-primary w-full justify-center"
                                    data-testid="contact-submit"
                                >
                                    {sending ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} strokeWidth={1.8} />}
                                    {sending ? "Sending…" : "Send message"}
                                </button>
                                <p className="text-xs text-[#6E7A74] text-center">
                                    Or email{" "}
                                    <a href="mailto:ashlee@asvmedicalservices.com" className="underline">
                                        ashlee@asvmedicalservices.com
                                    </a>{" "}
                                    directly.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="block text-sm" data-testid={testid}>
            <span className="text-[#4C5551] font-medium">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
